import { useMemo, useState, type JSX } from 'react';

import {
  Box,
  Checkbox,
  FormControlLabel,
  MenuItem,
  TextField,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import type { CalendarFilters as CalendarFiltersShape } from '@/api/types/Jizda';
import {
  RegistryAutocomplete,
  type RegistryValue,
} from '@/components/registry/RegistryAutocomplete';
import { useAppSelector } from '@/store/hooks';
import type { RootState } from '@/store';

// Tri-state assignment helper — mirrors OrdersFilters' pattern (vše / obsazené
// / nepřiřazené ↔ undefined / true / false).
type AssignmentValue = 'all' | 'assigned' | 'unassigned';

const assignmentToFilter = (v: AssignmentValue): boolean | undefined => {
  if (v === 'all') return undefined;
  return v === 'assigned';
};

const filterToAssignment = (assigned: boolean | undefined): AssignmentValue => {
  if (assigned === undefined) return 'all';
  return assigned ? 'assigned' : 'unassigned';
};

export interface CalendarFiltersProps {
  filters: CalendarFiltersShape;
  onChange: (next: Partial<CalendarFiltersShape>) => void;
}

// Calendar toolbar filters (CHR-03 §6 / ADR-0051): dispatcher, commodity,
// assignment tri-state, show-deleted, show-storno. Reuses the overview's
// tri-state helper pattern + the commodity `RegistryAutocomplete`. The
// dispatcher select is sourced from the already-loaded dispatchers cache
// (no duplicate fetch) so it lists every KC dispatcher by display name.
export const CalendarFilters = ({
  filters,
  onChange,
}: CalendarFiltersProps): JSX.Element => {
  const theme = useTheme();
  const { t } = useTranslation('orders');

  // Dispatcher options from the boot-loaded cache (dispatchersSlice). Select
  // the `byId` map (stable reference) and derive the sorted list under
  // `useMemo` so we don't return a fresh array from the selector every render
  // (which would trip RTK's selector-stability warning + cause rerenders).
  const dispatchersById = useAppSelector(
    (state: RootState) => state.dispatchers.byId,
  );
  const dispatcherOptions = useMemo(
    () =>
      Object.values(dispatchersById).sort((a, b) =>
        a.displayName.localeCompare(b.displayName, 'cs'),
      ),
    [dispatchersById],
  );

  const assignmentValue = filterToAssignment(filters.assigned);

  // Commodity filter (reuse OrdersFilters approach): only a resolved
  // `commodityId` (registry pick) feeds the query; clearing removes it.
  const [commodityValue, setCommodityValue] = useState<RegistryValue | null>(null);
  const handleCommodityChange = (next: RegistryValue | null): void => {
    setCommodityValue(next && next.id ? next : null);
    onChange({ commodityId: next?.id });
  };

  const dispatcherValue = useMemo(() => filters.dispatcher ?? 'all', [filters.dispatcher]);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        pt: theme.spacing(1.5),
        pb: theme.spacing(2),
        flexWrap: 'wrap',
      }}
    >
      <TextField
        select
        size="small"
        label={t('calendar.filter.dispatcher')}
        value={dispatcherValue}
        onChange={(e) =>
          onChange({
            dispatcher: e.target.value === 'all' ? undefined : e.target.value,
          })
        }
        sx={{ minWidth: 180 }}
        data-testid="calendar-filter-dispatcher"
      >
        <MenuItem value="all">{t('calendar.filter.dispatcherAll')}</MenuItem>
        {dispatcherOptions.map((d) => (
          <MenuItem key={d.sub} value={d.sub}>
            {d.displayName}
          </MenuItem>
        ))}
      </TextField>
      <Box sx={{ minWidth: 180 }} data-testid="calendar-filter-commodity">
        <RegistryAutocomplete
          kind="commodity"
          label={t('calendar.filter.commodity')}
          value={commodityValue}
          onChange={handleCommodityChange}
        />
      </Box>
      <TextField
        select
        size="small"
        label={t('calendar.filter.assignment')}
        value={assignmentValue}
        onChange={(e) =>
          onChange({ assigned: assignmentToFilter(e.target.value as AssignmentValue) })
        }
        sx={{ minWidth: 160 }}
        data-testid="calendar-filter-assignment"
      >
        <MenuItem value="all">{t('calendar.filter.assignmentAll')}</MenuItem>
        <MenuItem value="assigned">{t('calendar.filter.assignmentAssigned')}</MenuItem>
        <MenuItem value="unassigned">
          {t('calendar.filter.assignmentUnassigned')}
        </MenuItem>
      </TextField>
      <FormControlLabel
        control={
          <Checkbox
            checked={filters.showStorno}
            onChange={(e) => onChange({ showStorno: e.target.checked })}
            data-testid="calendar-filter-storno"
          />
        }
        label={t('calendar.filter.showStorno')}
        sx={{ '& .MuiFormControlLabel-label': { fontSize: 13 } }}
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={filters.showDeleted}
            onChange={(e) => onChange({ showDeleted: e.target.checked })}
            data-testid="calendar-filter-deleted"
          />
        }
        label={t('calendar.filter.showDeleted')}
        sx={{ '& .MuiFormControlLabel-label': { fontSize: 13 } }}
      />
    </Box>
  );
};
